// RC50 V5-D NAV-001/007/008/010/011 — frontend side of the ONE governed navigation contract.
// Targets are produced server-side (services/copilot/navigation.py); the UI only verifies
// they are in-app application destinations, navigates, and never renders "#" controls.
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCopilot } from "@/context/CopilotContext";

const MODULE_PREFIXES = ["/dealer", "/admin"];

export function isGovernedTarget(t) {
  if (typeof t !== "string" || !t.startsWith("/")) return false;
  const path = t.split("?")[0];
  return MODULE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

export const isMobileViewport = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

// Navigate from Copilot: collapse the panel on mobile so the destination/focus is visible (NAV-011).
export function useGovernedNavigate() {
  const navigate = useNavigate();
  const { closePanel } = useCopilot();
  return useCallback((target) => {
    if (!isGovernedTarget(target)) return false;
    if (isMobileViewport()) closePanel();
    navigate(target, { state: { from: "copilot" } });  // NAV-010 origin travels as router state
    return true;
  }, [navigate, closePanel]);
}

// NAV-010 origin contract: router state {from:"copilot"} (or the Library's legacy ?from=copilot) → "Back to Copilot".
export function useCopilotOrigin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCopilot = params.get("from") === "copilot" || location.state?.from === "copilot";
  const back = useCallback(() => {
    if (fromCopilot && window.history.length > 1) navigate(-1);
    else navigate(location.pathname.split("/").slice(0, 3).join("/") || "/dealer");
  }, [fromCopilot, navigate, location.pathname]);
  return { fromCopilot, back };
}

// NAV-004/006: bring the exact focused row into view once data is loaded.
export function useFocusScroll(elementId, ready) {
  useEffect(() => {
    if (!ready || !elementId) return;
    const el = document.getElementById(elementId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [elementId, ready]);
}
